import React, { useCallback, useRef, useState, useEffect } from 'react';
import DocsSidebar from './DocsSidebar';
import DocsContent from './DocsContent';
import DocsHeader from './DocsHeader';
import { resolveDocumentPortalPath, splitDocsPathHash } from '../utils/docsPaths';

const DocsViewer = ({ docsType = 'frontend', title = 'Documentation' }) => {
  const [currentFile, setCurrentFile] = useState(null);
  const [content, setContent] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Always open by default
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [redirectInfo, setRedirectInfo] = useState(null);
  const [documentMetadataByPath, setDocumentMetadataByPath] = useState({});
  const redirectsRef = useRef(null);

  const loadRedirects = async () => {
    if (redirectsRef.current) return redirectsRef.current;
    try {
      const response = await fetch('/generated/docs-redirects.json');
      if (!response.ok) {
        redirectsRef.current = {};
        return redirectsRef.current;
      }
      redirectsRef.current = await response.json();
      return redirectsRef.current;
    } catch {
      redirectsRef.current = {};
      return redirectsRef.current;
    }
  };

  const loadExternalMarkdownManifest = async () => {
    try {
      const response = await fetch('/generated/external-markdown-manifest.json');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  };

  const resolveRedirect = async (filename) => {
    const redirects = await loadRedirects();
    const { path, hash } = splitDocsPathHash(filename);
    const redirectedPath = redirects[path];
    if (!redirectedPath) {
      return { filename, redirect: null };
    }
    return {
      filename: `${redirectedPath}${hash}`,
      redirect: {
        from: filename,
        to: `${redirectedPath}${hash}`
      }
    };
  };

  const loadFile = async (filename) => {
    setIsLoading(true);
    try {
      const resolved = await resolveRedirect(filename);
      const response = await fetch(resolved.filename);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const markdown = await response.text();
      setContent(markdown);
      setCurrentFile(resolved.filename);
      setRedirectInfo(resolved.redirect);
      // Only close sidebar on mobile after selection
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      }
    } catch (error) {
      console.error('Error loading file:', error);
      setContent(`# Error Loading File

Could not load **${filename}**.

**Error:** ${error.message}

Make sure you're running this from a local server or the file exists.`);
      setCurrentFile(filename);
      setRedirectInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWelcomeContent = useCallback(() => {
    const welcomeContent = docsType === 'api'
      ? `# Welcome to Promang API Documentation

Complete API reference for Promang backend services.

## 🚀 Getting Started

- **Authentication**: JWT-based API authentication
- **Base URL**: \`/api/v1/\`
- **Format**: JSON responses
- **Rate Limiting**: Applied to all endpoints

## 🛡️ HSE/KS Compliance

This API includes comprehensive HSE/KS modules for ISO 9001 compliance:

- ✅ Context & Climate Change Management
- ✅ Controlled Documents & Version Control
- ✅ Management Events & CAPA System
- ✅ Risk Assessment & Job Safety Analysis
- ✅ Competency Management
- ✅ Audit & Management Review Systems
- ✅ KPI Monitoring

## 📚 Available Modules

**👉 Use the sidebar on the left to navigate through available documentation.**

The sidebar contains:
- **Modules**: Project, Report, Order, Customer, User, and more
- **HSE/KS**: Health, Safety, Environment, and Quality modules
- **Core Systems**: Document system, PDF generation, background jobs
- **Integrations**: SVV, Phonero, and other third-party integrations

Click on any item in the sidebar to load the documentation.

---

*Built with Ruby on Rails & ActiveRecord*`
      : `# Welcome to Promang Frontend Documentation

Complete guide for Promang React frontend development.

## 🚀 Getting Started

- **Framework**: React 18 with TypeScript
- **Styling**: Tailwind CSS
- **State**: Redux Toolkit + RTK Query
- **Routing**: React Router v6

## 🧩 Core Features

- **Component Library**: Reusable UI components
- **API Integration**: RTK Query for data fetching
- **Form Handling**: React Hook Form with validation
- **Authentication**: JWT-based user management

## 📱 Responsive Design

All components are mobile-first and fully responsive across all device sizes.

## 📚 Available Documentation

**👉 Use the sidebar on the left to navigate through available documentation.**

The sidebar contains:
- **Getting Started**: Overview, installation, quick start
- **Core Features**: Authentication, dashboard, routing, state management
- **API Integration**: API structure and endpoints
- **Components**: UI components, forms, tables
- **Modules**: Time registration, absence, vehicle service, HSE/KS
- **Development**: Development guide, testing, deployment

Click on any item in the sidebar to load the documentation.

---

*Built with React & Tailwind CSS*`;

    setContent(welcomeContent);
    setCurrentFile(null);
    setRedirectInfo(null);
  }, [docsType]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  useEffect(() => {
    loadWelcomeContent();
  }, [loadWelcomeContent]);

  useEffect(() => {
    let mounted = true;

    async function loadDocumentMetadata() {
      try {
        const response = await fetch('/generated/documentation-index.json');
        if (!response.ok) return;
        const payload = await response.json();
        const externalManifest = await loadExternalMarkdownManifest();
        const byPath = {};
        (payload.documents ?? []).forEach((document) => {
          const portalPath = resolveDocumentPortalPath(document, externalManifest);
          if (portalPath) byPath[portalPath] = document;
        });
        if (mounted) setDocumentMetadataByPath(byPath);
      } catch {
        if (mounted) setDocumentMetadataByPath({});
      }
    }

    loadRedirects();
    loadDocumentMetadata();
    return () => {
      mounted = false;
    };
  }, []);

  const currentMetadata = currentFile
    ? documentMetadataByPath[splitDocsPathHash(currentFile).path] ?? null
    : null;

  return (
    <div className="flex h-screen bg-gray-50">
      <DocsSidebar
        docsType={docsType}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onFileSelect={loadFile}
        searchQuery={searchQuery}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <DocsHeader
          title={title}
          onSearch={handleSearch}
          onToggleSidebar={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          docsType={docsType}
          onSearchResultSelect={loadFile}
        />

        <DocsContent
          content={content}
          currentFile={currentFile}
          isLoading={isLoading}
          onFileSelect={loadFile}
          redirectInfo={redirectInfo}
          metadata={currentMetadata}
        />
      </div>
    </div>
  );
};

export default DocsViewer;

